import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ChatMessagesContext } from './chatMessagesContext';
import type { ChatMessagesContextValue } from '../../../types/chatMessagesContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../auth/useAuth';
import { useI18n } from '../../../i18n/useI18n';
import {
  mergePlatformChatMessagesIntoCache,
  patchPlatformChatSessionPreview,
} from '../platformChatMessagesCache';
import { platformChatService } from '../../../services/PlatformChatService';
import type { ChatMessage } from '../../../types/chatMessage';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import { clearOptimisticSessionMessages } from './chatMessagesProviderHelpers';
import { runPlatformChatSendTurn } from './runPlatformChatSendTurn';

const EMPTY_OPTIMISTIC_MESSAGES: ChatMessage[] = [];

export function ChatMessagesProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { translate } = useI18n();
  const { platformChatMaxResults, platformChatMinScore, isUserConfigReady } =
    useUserConfig();
  const [optimisticMessagesBySessionId, setOptimisticMessagesBySessionId] =
    useState<Record<string, ChatMessage[]>>({});
  const [sendingSessionIds, setSendingSessionIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const sendingSessionIdsRef = useRef(new Set<string>());

  const beginSending = useCallback((sessionId: string) => {
    sendingSessionIdsRef.current.add(sessionId);
    setSendingSessionIds(new Set(sendingSessionIdsRef.current));
  }, []);

  const endSending = useCallback((sessionId: string) => {
    sendingSessionIdsRef.current.delete(sessionId);
    setSendingSessionIds(new Set(sendingSessionIdsRef.current));
  }, []);

  const getOptimisticMessages = useCallback<
    ChatMessagesContextValue['getOptimisticMessages']
  >(
    (sessionId) =>
      optimisticMessagesBySessionId[sessionId] ?? EMPTY_OPTIMISTIC_MESSAGES,
    [optimisticMessagesBySessionId],
  );

  const isSendingMessage = useCallback<
    ChatMessagesContextValue['isSendingMessage']
  >((sessionId) => sendingSessionIds.has(sessionId), [sendingSessionIds]);

  const sendMessage = useCallback<ChatMessagesContextValue['sendMessage']>(
    async (sessionId, content, options) => {
      if (sendingSessionIdsRef.current.has(sessionId) || !isUserConfigReady) {
        return;
      }

      await runPlatformChatSendTurn({
        queryClient,
        sessionId,
        content,
        options: {
          ...options,
          maxResults: platformChatMaxResults,
          minScore: platformChatMinScore,
        },
        userId: user?.id,
        userName: user?.name,
        youBadgeLabel: translate('common.youBadge'),
        assistantName: translate('platformChat.assistantName'),
        setOptimisticMessagesBySessionId,
        beginSending,
        endSending,
      });
    },
    [
      beginSending,
      endSending,
      isUserConfigReady,
      platformChatMaxResults,
      platformChatMinScore,
      queryClient,
      translate,
      user?.id,
      user?.name,
    ],
  );

  const retryMessage = useCallback<ChatMessagesContextValue['retryMessage']>(
    async (sessionId, messageId) => {
      if (sendingSessionIdsRef.current.has(sessionId) || !isUserConfigReady) {
        return;
      }

      const optimisticAssistantMessageId = `local-assistant-${Date.now()}`;
      const optimisticAssistantMessage: ChatMessage = {
        id: optimisticAssistantMessageId,
        sessionId,
        content: '',
        sentAt: new Date().toISOString(),
        author: {
          type: 'ai',
          name: translate('platformChat.assistantName'),
        },
        isPendingAssistantReply: true,
        isStreamingAssistantReply: false,
      };

      beginSending(sessionId);
      setOptimisticMessagesBySessionId((current) => ({
        ...current,
        [sessionId]: [optimisticAssistantMessage],
      }));

      try {
        const regenerated = await platformChatService.regenerateReply(
          sessionId,
          messageId,
          {
            maxResults: platformChatMaxResults,
            minScore: platformChatMinScore,
          },
        );
        if (regenerated) {
          mergePlatformChatMessagesIntoCache(queryClient, sessionId, [
            regenerated,
          ]);
          patchPlatformChatSessionPreview(queryClient, sessionId, regenerated);
        }
      } finally {
        clearOptimisticSessionMessages(
          setOptimisticMessagesBySessionId,
          sessionId,
        );
        endSending(sessionId);
      }
    },
    [
      beginSending,
      endSending,
      isUserConfigReady,
      platformChatMaxResults,
      platformChatMinScore,
      queryClient,
      translate,
    ],
  );

  const value: ChatMessagesContextValue = {
    getOptimisticMessages,
    isSendingMessage,
    sendMessage,
    retryMessage,
  };

  return (
    <ChatMessagesContext.Provider value={value}>
      {children}
    </ChatMessagesContext.Provider>
  );
}
